import { Controller } from "@hotwired/stimulus"

// Pre-built industry packages picker (Step 21).
// Two screens inside the modal:
//   1. picker  — fetched card grid of available packages
//   2. confirm — per-section strategy radios + Install button
// Install POSTs to PortfolioPackagesController#install with the selected slug
// + per-section strategies. The list itself comes from #index on first open.
//
// Section icons + labels match the Step 20 import modal so a user who just
// installed via that modal sees a familiar layout.
const SECTIONS = [
  { key: "theme", label: "Theme", icon: "paint-brush" },
  { key: "rag_thresholds", label: "RAG thresholds", icon: "adjustments-horizontal" },
  { key: "filter_presets", label: "Filter presets", icon: "rectangle-stack" },
  { key: "alert_rules", label: "Alert rules", icon: "bell" },
  { key: "scheduled_reports", label: "Scheduled reports", icon: "clock" }
]

export default class extends Controller {
  static targets = [
    "pickerScreen", "cardGrid", "cardTemplate", "emptyState",
    "confirmScreen", "confirmName", "confirmVertical", "confirmDescription",
    "strategyList", "strategyRowTemplate",
    "loadingScreen", "loadingLabel",
    "errors", "installButton"
  ]
  static values = { listUrl: String, installUrlTemplate: String }

  connect() {
    this._packages = []
    this._packagesLoaded = false
    this._selected = null
    this._strategies = {}
    this._observer = new MutationObserver(() => {
      if (!this.element.classList.contains("hidden")) this._onModalOpen()
    })
    this._observer.observe(this.element, { attributes: true, attributeFilter: ["class"] })
    // After install we reload, so the install toast has to survive the
    // navigation — stashed in sessionStorage and replayed here on connect.
    this._showPendingFlash()
  }

  disconnect() {
    this._observer?.disconnect()
  }

  _onModalOpen() {
    this._selected = null
    this._strategies = {}
    this.errorsTarget.classList.add("hidden")
    this._showPicker()
    if (!this._packagesLoaded) this._loadPackages()
  }

  // ── Load + render package cards ──────────────────────────────────

  async _loadPackages() {
    this._showLoading("Loading packages…")
    try {
      const res = await fetch(this.listUrlValue, {
        method: "GET",
        credentials: "same-origin",
        headers: { "Accept": "application/json" }
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) {
        const msg = Array.isArray(json.errors) ? json.errors.join("; ") : `HTTP ${res.status}`
        this._renderError(msg)
        this._showPicker()
        return
      }
      this._packages = Array.isArray(json.packages) ? json.packages : []
      this._packagesLoaded = true
      this._renderCards()
      this._showPicker()
    } catch (err) {
      console.error("Package load failed", err)
      this._renderError("Could not load packages.")
      this._showPicker()
    }
  }

  _renderCards() {
    this.cardGridTarget.innerHTML = ""
    if (this._packages.length === 0) {
      this.emptyStateTarget.classList.remove("hidden")
      return
    }
    this.emptyStateTarget.classList.add("hidden")

    this._packages.forEach((pkg) => {
      const card = this.cardTemplateTarget.content.firstElementChild.cloneNode(true)
      card.dataset.slug = pkg.slug

      card.querySelector('[data-role="vertical-badge"]').textContent = pkg.vertical || "Industry"
      card.querySelector('[data-role="package-name"]').textContent = pkg.name || pkg.slug
      card.querySelector('[data-role="package-description"]').textContent = pkg.description || ""

      const swatch = card.querySelector('[data-role="color-swatch"]')
      if (pkg.primary_color) swatch.style.backgroundColor = pkg.primary_color
      else swatch.classList.add("hidden")

      const highlights = card.querySelector('[data-role="highlights"]')
      ;(pkg.highlights || []).slice(0, 4).forEach((line) => {
        const li = document.createElement("li")
        li.className = "flex items-start gap-1.5"
        li.innerHTML = `<span class="text-[var(--portfolio-primary)] mt-1.5 w-1 h-1 rounded-full bg-current shrink-0"></span><span>${this._escape(line)}</span>`
        highlights.appendChild(li)
      })

      const counts = pkg.counts || {}
      const parts = []
      if (counts.filter_presets) parts.push(`${counts.filter_presets} preset${counts.filter_presets === 1 ? "" : "s"}`)
      if (counts.alert_rules) parts.push(`${counts.alert_rules} alert${counts.alert_rules === 1 ? "" : "s"}`)
      if (counts.scheduled_reports) parts.push(`${counts.scheduled_reports} report${counts.scheduled_reports === 1 ? "" : "s"}`)
      card.querySelector('[data-role="counts"]').textContent = parts.join(" · ") || "—"

      card.querySelector('[data-role="threshold-profile"]').textContent = `${pkg.threshold_profile || "balanced"} thresholds`

      this.cardGridTarget.appendChild(card)
    })
  }

  // ── Card → confirm screen ────────────────────────────────────────

  select(event) {
    const card = event.currentTarget.closest("[data-slug]")
    if (!card) return
    const slug = card.dataset.slug
    const pkg = this._packages.find((p) => p.slug === slug)
    if (!pkg) return

    this._selected = pkg
    this._strategies = SECTIONS.reduce((acc, s) => {
      acc[s.key] = pkg.default_strategy || "merge"
      return acc
    }, {})

    this.confirmNameTarget.textContent = pkg.name
    this.confirmVerticalTarget.textContent = pkg.vertical
    this.confirmDescriptionTarget.textContent = pkg.description
    this._renderStrategyRows(pkg)

    this._showConfirm()
  }

  _renderStrategyRows(pkg) {
    this.strategyListTarget.innerHTML = ""
    SECTIONS.forEach((section) => {
      const row = this.strategyRowTemplateTarget.content.firstElementChild.cloneNode(true)
      row.dataset.section = section.key

      // Insert the icon SVG inline. We mirror the Step 20 modal's styling
      // (text-gray-500 small heroicons-outline) so the two install paths
      // feel the same.
      const iconEl = row.querySelector('[data-role="row-icon"]')
      const iconNode = this._iconNode(section.icon)
      if (iconNode) iconEl.replaceWith(iconNode)
      row.querySelector('[data-role="row-label"]').textContent = section.label
      row.querySelector('[data-role="row-status"]').textContent = this._summarizeSection(pkg, section.key)

      row.querySelectorAll('[data-role="strategy-radio"]').forEach((input) => {
        input.name = `strategy_${section.key}`
        input.dataset.section = section.key
        input.checked = input.value === this._strategies[section.key]
      })
      this.strategyListTarget.appendChild(row)
    })
  }

  _summarizeSection(pkg, key) {
    if (key === "theme") {
      return pkg.primary_color ? `Sets primary ${pkg.primary_color}` : "Includes theme"
    }
    if (key === "rag_thresholds") {
      return `Sets ${pkg.threshold_profile} profile`
    }
    const counts = pkg.counts || {}
    const n = counts[key] || 0
    if (n === 0) return "Nothing in this package"
    return `${n} ${key === "filter_presets" ? "preset" : key === "alert_rules" ? "alert" : "report"}${n === 1 ? "" : "s"}`
  }

  _iconNode(name) {
    // Heroicons-outline path strings live on the server icon helper. We can't
    // re-render an ERB partial from JS, so the modal pre-renders one of each
    // SVG into a hidden bank we read by name. Fallback: empty span.
    const bank = document.getElementById("portfolio-packages-icon-bank")
    if (!bank) return null
    const src = bank.querySelector(`[data-icon="${name}"]`)
    return src ? src.cloneNode(true) : null
  }

  strategyChanged(event) {
    const section = event.currentTarget.dataset.section
    const value = event.currentTarget.value
    if (!section || !value) return
    this._strategies[section] = value
  }

  back(event) {
    event?.preventDefault()
    this._selected = null
    this._strategies = {}
    this._showPicker()
  }

  // ── Install ──────────────────────────────────────────────────────

  async install(event) {
    event?.preventDefault()
    if (!this._selected) return

    this.installButtonTarget.disabled = true
    this._showLoading(`Installing ${this._selected.name}…`)
    try {
      const url = this.installUrlTemplateValue.replace("__SLUG__", encodeURIComponent(this._selected.slug))
      const res = await fetch(url, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this._csrf()
        },
        body: JSON.stringify({ conflict_strategies: this._strategies })
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || json.success === false) {
        const msg = Array.isArray(json.errors) ? json.errors.join("; ") : `HTTP ${res.status}`
        this._renderError(msg)
        this.installButtonTarget.disabled = false
        this._showConfirm()
        return
      }

      const counts = this._summarizeCounts(json.counts)
      if (json.no_op) {
        // Nothing changed — no reload needed; show the toast inline.
        this._toast("No changes — package matches current configuration.", "notice")
        this._close()
      } else {
        // Multiple toolbar surfaces (presets, alerts, scheduled reports)
        // read state from server-rendered partials, so we reload to
        // surface the new config. The toast wouldn't survive that, so
        // stash it in sessionStorage and replay it on next connect.
        this._flashAndReload(`${this._selected.name} installed (${counts}).`, "notice")
      }
    } catch (err) {
      console.error("Package install failed", err)
      this._renderError("Could not install the package.")
      this.installButtonTarget.disabled = false
      this._showConfirm()
    }
  }

  _summarizeCounts(counts) {
    const sections = Object.keys(counts || {})
    const total = sections.reduce((n, s) => {
      const bucket = counts[s] || {}
      return n + Object.keys(bucket).reduce((x, k) => k === "skip" ? x : x + bucket[k], 0)
    }, 0)
    return `${total} change${total === 1 ? "" : "s"}`
  }

  // ── Screen toggles ───────────────────────────────────────────────

  _showPicker() {
    this.pickerScreenTarget.classList.remove("hidden")
    this.confirmScreenTarget.classList.add("hidden")
    this.loadingScreenTarget.classList.add("hidden")
    this.installButtonTarget.classList.add("hidden")
  }

  _showConfirm() {
    this.pickerScreenTarget.classList.add("hidden")
    this.confirmScreenTarget.classList.remove("hidden")
    this.loadingScreenTarget.classList.add("hidden")
    this.installButtonTarget.classList.remove("hidden")
    this.installButtonTarget.disabled = false
  }

  _showLoading(label) {
    this.loadingLabelTarget.textContent = label || "Loading…"
    this.pickerScreenTarget.classList.add("hidden")
    this.confirmScreenTarget.classList.add("hidden")
    this.loadingScreenTarget.classList.remove("hidden")
    this.installButtonTarget.classList.add("hidden")
    this.errorsTarget.classList.add("hidden")
  }

  _renderError(msg) {
    this.errorsTarget.textContent = msg
    this.errorsTarget.classList.remove("hidden")
  }

  _close() {
    document.getElementById("portfolio-packages-modal")?.classList.add("hidden")
    document.body.style.overflow = ""
  }

  _csrf() {
    return document.querySelector("meta[name=csrf-token]")?.content || ""
  }

  _escape(s) {
    return String(s ?? "").replace(/[&<>"']/g, (c) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]))
  }

  _toast(message, type = "notice") {
    const el = document.createElement("div")
    el.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.textContent = message
    document.body.prepend(el)
  }

  _flashAndReload(message, type = "notice") {
    sessionStorage.setItem("portfolio_packages_flash", JSON.stringify({ message, type }))
    window.location.reload()
  }

  _showPendingFlash() {
    const raw = sessionStorage.getItem("portfolio_packages_flash")
    if (!raw) return
    sessionStorage.removeItem("portfolio_packages_flash")
    try {
      const { message, type } = JSON.parse(raw)
      this._toast(message, type)
    } catch { /* ignore */ }
  }
}
